import React, { useEffect, useMemo, useRef, useState } from "react";
import { Tooltip } from "antd";
import {
  Container,
  DayCell,
  DaysGrid,
  HeaderCell,
  HeaderRow,
  HeaderDayMonth,
  TalhaoCell,
  Status,
  ContainerStatus,
  WrapperDaysGrid,
  Body,
  JanelaBarsContainer,
  JanelaBar,
  CardColor,
  ContainerStatusTooltip,
  SpanCounter,
} from "./styles";
import useModalSchedule from "../../../../hooks/useModalSchedule";
import useAgendaCampo, { AgendaWindowItem } from "../../../../hooks/useAgendaCampo";
import { addDays, addMonths, compareDate, dayOfMonth, formatDayMonth, getDaysInMonth, getWeeksInMonth, isToday, parseDateString } from "../../utils";
import { subDays } from "date-fns";

type CalendarViewMode = "month" | "week" | "talhao" | "janela-execucao";
type CalendarDataRecord = {
  talhaoId?: string;
  talhao?: string;
  dataProgramada?: string;
  periodoDataInicio?: string;
  periodoDataFim?: string;
  totalAreaAtrasada: number;
  totalAreaExecutada: number;
  totalAreaExecutadaParcial: number;
  totalAreaPlanejada: number;
};

type StatusCheckbox = {
    statusPlanejado: boolean,
    statusExecutadoParcialmente: boolean,
    statusExecutado: boolean,
    statusAtrasado: boolean,
}

type CalendarProps = {
  currentDate?: Date | string;
  viewMode?: CalendarViewMode;
  navigationOffset?: number;
  data: CalendarDataRecord[];
  talhaoField?: string;
  status: StatusCheckbox,
};

type CalendarDay = {
  date: Date;
  dayNumber: number;
  isCurrentMonth: boolean;
};

const WEEK_DAYS = [
  "Domingo",
  "Segunda-Feira",
  "Terça-Feira",
  "Quarta-Feira",
  "Quinta-Feira",
  "Sexta-Feira",
  "Sábado",
];

const DEFAULT_WEEKS_IN_MONTH = 5;
const WEEK_CELLS_TOTAL = 7;
const MAX_BARRAS_DIA = 5;

const isMonthLike = (viewMode: CalendarViewMode) =>
  viewMode === "month" || viewMode === "janela-execucao";

const toIsoDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const isWindowActiveOnDay = (window: AgendaWindowItem, iso: string) =>
  iso >= window.periodoDataInicioRaw && iso <= window.periodoDataFimRaw;

const getBarVariant = (
  window: AgendaWindowItem,
  iso: string
): "single" | "start" | "end" | "continuation" => {
  const isStart = iso === window.periodoDataInicioRaw;
  const isEnd = iso === window.periodoDataFimRaw;
  if (isStart && isEnd) return "single";
  if (isStart) return "start";
  if (isEnd) return "end";
  return "continuation";
};

const normalizeInputDate = (inputDate?: Date | string): Date => {
  if (!inputDate) {
    return new Date();
  }

  if (inputDate instanceof Date) {
    return new Date(
      inputDate.getFullYear(),
      inputDate.getMonth(),
      inputDate.getDate(),
    );
  }

  const parsedByPattern = inputDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (parsedByPattern) {
    const [, year, month, day] = parsedByPattern;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  const parsedDate = new Date(inputDate);
  if (!Number.isNaN(parsedDate.getTime())) {
    return new Date(
      parsedDate.getFullYear(),
      parsedDate.getMonth(),
      parsedDate.getDate(),
    );
  }

  return new Date();
};

const Calendar: React.FC<CalendarProps> = ({
  currentDate,
  viewMode = "month",
  navigationOffset = 0,
  data = [],
  talhaoField = "talhao",
  status
}) => {
  const { statusPlanejado, statusExecutadoParcialmente, statusExecutado, statusAtrasado} = status;
  const { setData, setDatas, setTalhao,setIsOpen, setTalhaoCode } = useModalSchedule();
  const { listWindowExecution, selectedWindowIds } = useAgendaCampo();
  const wrapperDaysGridRef = useRef<HTMLDivElement>(null);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);

  const selectedWindows = useMemo(() => {
    const selectedSet = new Set(selectedWindowIds);
    return listWindowExecution
      .filter((item) => selectedSet.has(item.id))
      .sort(
        (a, b) =>
          a.periodoDataInicioRaw.localeCompare(b.periodoDataInicioRaw) ||
          a.id.localeCompare(b.id)
      );
  }, [listWindowExecution, selectedWindowIds]);

  useEffect(() => {
    if (viewMode !== "talhao") {
      setScrollbarWidth(0);
      return;
    }

    const wrapper = wrapperDaysGridRef.current;
    if (!wrapper) return;

    const measureScrollbar = () => {
      setScrollbarWidth(wrapper.offsetWidth - wrapper.clientWidth);
    };

    measureScrollbar();

    const resizeObserver = new ResizeObserver(measureScrollbar);
    resizeObserver.observe(wrapper);

    return () => resizeObserver.disconnect();
  }, [viewMode, data.length]);

  const referenceDate = useMemo(() => {
    const baseDate = normalizeInputDate(currentDate);

    if (viewMode === "week" || viewMode === "talhao") {
      return addDays(baseDate, navigationOffset * 7);
    }

    return addMonths(baseDate, navigationOffset);
  }, [currentDate, navigationOffset, viewMode]);

  const weeksInMonth = useMemo(() => {
    if (!isMonthLike(viewMode)) return DEFAULT_WEEKS_IN_MONTH;

    const year = referenceDate.getFullYear();
    const month = referenceDate.getMonth();
    return getWeeksInMonth(year, month);
  }, [referenceDate, viewMode]);

  const cellsTotal = weeksInMonth * WEEK_CELLS_TOTAL;

  const days = useMemo<CalendarDay[]>(() => {
    
    if (viewMode === "week" || viewMode === "talhao") {
      const startOfWeek = addDays(referenceDate, -referenceDate.getDay());
      const weekDays: CalendarDay[] = [];

      for (let index = 0; index < WEEK_CELLS_TOTAL; index += 1) {
        const date = addDays(startOfWeek, index);
        weekDays.push({
          date,
          dayNumber: date.getDate(),
          isCurrentMonth: date.getMonth() === referenceDate.getMonth(),
        });
      }

      return weekDays;
    }

    const year = referenceDate.getFullYear();
    const month = referenceDate.getMonth();

    const firstDayOfMonth = new Date(year, month, 1);
    const firstWeekDayIndex = firstDayOfMonth.getDay();
    const daysInCurrentMonth = getDaysInMonth(year, month);
    const daysInPreviousMonth = getDaysInMonth(year, month - 1);

    const calendarDays: CalendarDay[] = [];

    for (let index = 0; index < cellsTotal; index += 1) {
      const dayOffset = index - firstWeekDayIndex;
      
      if (dayOffset < 0) {
        const dayNumber = daysInPreviousMonth + dayOffset + 1;

        calendarDays.push({
          date: new Date(year, month - 1, dayNumber),
          dayNumber,
          isCurrentMonth: false,
        });
        continue;
      }

      if (dayOffset < daysInCurrentMonth) {
        const dayNumber = dayOffset + 1;
        calendarDays.push({
          date: new Date(year, month, dayNumber),
          dayNumber,
          isCurrentMonth: true,
        });
        continue;
      }

      const dayNumber = dayOffset - daysInCurrentMonth + 1;
      calendarDays.push({
        date: new Date(year, month + 1, dayNumber),
        dayNumber,
        isCurrentMonth: false,
      });
    }

    return calendarDays;
  }, [referenceDate, viewMode, cellsTotal]);

const orderedDays = useMemo(() => {
  if (!currentDate || !isMonthLike(viewMode)) {
    return days;
  }

  const baseDate = normalizeInputDate(currentDate);

  const targetDay = days.find(day =>
    day.date.getFullYear() === baseDate.getFullYear() &&
    day.date.getMonth() === baseDate.getMonth() &&
    day.date.getDate() === baseDate.getDate()
  );

  if (!targetDay) return days;

  const startOfWeek = addDays(targetDay.date, -targetDay.date.getDay());

  const newDays: CalendarDay[] = [];

  const rangeStart = baseDate;
  
  const today = typeof currentDate !== 'string' ? currentDate : new Date(currentDate) as Date // new Date();
  const yearCurrent = today.getFullYear();
  const monthCurrent = today.getMonth() +1;
  const start: Date = currentDate ? parseDateString(currentDate as string) : new Date(`${yearCurrent}-${monthCurrent}-01`);
  const counter = isMonthLike(viewMode) ? dayOfMonth(start) : 7;

  const end: Date = addDays(currentDate ?new Date(currentDate) : new Date(`${yearCurrent}-${monthCurrent}-01`), counter);
  
  for (let i = 0; i < cellsTotal; i++) {
    const date = addDays(startOfWeek, i);
    const isInRange =
    date >= rangeStart && date <= end;

    newDays.push({
      date,
      dayNumber: date.getDate(),
      isCurrentMonth:  isInRange, //date.getMonth() === baseDate.getMonth(),
    });
  }

  return newDays;
}, [days, currentDate, viewMode, cellsTotal]);

  return (
    <Container $viewMode={viewMode}>
      <HeaderRow $viewMode={viewMode} $scrollbarWidth={scrollbarWidth}>
        {viewMode === "talhao" && <HeaderCell>Talhão</HeaderCell>}
        {WEEK_DAYS.map((weekDay, idx) => {
          const dayForHeader = isMonthLike(viewMode) ? undefined : days[idx];

          return (
            <HeaderCell key={weekDay}>
              {weekDay}
              {!isMonthLike(viewMode) ? (
                dayForHeader ? (
                  <HeaderDayMonth>{formatDayMonth(dayForHeader.date)}</HeaderDayMonth>
                ) : (
                  <HeaderDayMonth>-</HeaderDayMonth>
                )
              ) : null}
            </HeaderCell>
          );
        })}
      </HeaderRow>
      <Body>
        {viewMode !== "talhao" && (
        <DaysGrid $viewMode={viewMode} $weeksInMonth={weeksInMonth}>
          {orderedDays.map((day) => {
            const dayIso = toIsoDate(day.date);
            const windowsOnDay =
              viewMode === "janela-execucao"
                ? selectedWindows.filter((window) => isWindowActiveOnDay(window, dayIso))
                : [];

            let isClicked = false;
            if (viewMode !== "janela-execucao") {
              isClicked = data?.some(item => item.dataProgramada && compareDate(day.date, item.dataProgramada));
            }

            const slots = selectedWindows.slice(0, MAX_BARRAS_DIA);
            const depoisDoQuinto = Math.max(0, selectedWindows.length - MAX_BARRAS_DIA);
            return (
              <DayCell
                key={day.date.toISOString()}
                $isCurrentMonth={day.isCurrentMonth}
                today={isToday(day.date)}
                $isClicked={isClicked || !!windowsOnDay?.[0]?.periodoDataInicioRaw}
                onClick={() => {
                  if (isClicked) {
                    setData(day.date.toISOString());
                    setIsOpen(true);
                  }
                }}
                todayCurrent={isToday(day.date)}
                $viewMode={viewMode}
                $weeksInMonth={weeksInMonth}
              >
                {isMonthLike(viewMode) && <div>{day.dayNumber}</div>}
                {viewMode === "janela-execucao" && windowsOnDay.length > 0 && (
                  <Tooltip overlayStyle={{maxWidth: 440}} title={
                    windowsOnDay.map((window) => <ContainerStatusTooltip><CardColor color={window.color} />{window.periodoDataInicio} à {window.periodoDataFim} - {window.operacaoClasse}</ContainerStatusTooltip>)
                  }>
                    <JanelaBarsContainer onClick={() => {
                      if (windowsOnDay?.[0]?.periodoDataInicioRaw) {
                        setData(day.date.toISOString());
                        const datas = windowsOnDay.map(window => ({
                          PeriodoDataInicio: window.periodoDataInicioRaw,
                          PeriodoDataFim: window.periodoDataFimRaw
                        }))
                        setDatas(datas);
                        setIsOpen(true);
                      }
                    }}>
                      {slots.map((window) => {
                        const active = isWindowActiveOnDay(window, dayIso);
                        return (
                          <JanelaBar
                            key={window.id}
                            $color={window.color}
                            $variant={active ? getBarVariant(window, dayIso) : "continuation"}
                            $empty={!active}
                            data-empty={!active ? "true" : undefined}
                          />
                        );
                      })}
                      {windowsOnDay.length > MAX_BARRAS_DIA && (
                        <SpanCounter>+ {depoisDoQuinto}</SpanCounter>
                      )}
                    </JanelaBarsContainer>
                  </Tooltip>
                )}
                {viewMode !== "janela-execucao" &&
                  data?.map(
                    (item) =>
                      item.dataProgramada &&
                      compareDate(day.date, item.dataProgramada) && (
                        <ContainerStatus key={`${item.dataProgramada}-${item.talhaoId || item.talhao || "status"}`}>
                          {item.totalAreaAtrasada > 0 && (
                            <Status color="#f44336">Atrasado - {item.totalAreaAtrasada} ha</Status>
                          )}
                          {item.totalAreaExecutada > 0 && (
                            <Status color="#4caf50">Executado - {item.totalAreaExecutada} ha</Status>
                          )}
                          {item.totalAreaExecutadaParcial > 0 && (
                            <Status color="#ff9800">
                              Executado parcial - {item.totalAreaExecutadaParcial} ha
                            </Status>
                          )}
                          {item.totalAreaPlanejada > 0 && (
                            <Status color="#2196f3">Planejado - {item.totalAreaPlanejada} ha</Status>
                          )}
                        </ContainerStatus>
                      )
                  )}
              </DayCell>
          );
          })}
        </DaysGrid>
        )}
      </Body>
      
      {viewMode === "talhao" &&
      <WrapperDaysGrid ref={wrapperDaysGridRef}>
        {data.map((item, index) => (
          <DaysGrid $viewMode={viewMode} key={index}>
          <TalhaoCell>{item.talhao}</TalhaoCell>
          {days.map((day) => {
            const toMorrow = addDays(day.date, +1);
            const yesterday = subDays(day.date, -1);
            const dataProgramada = item.dataProgramada || "";
            const isClicked = !!dataProgramada && compareDate(day.date, dataProgramada);
            return (
              <DayCell
                $viewMode={viewMode}
                key={day.date.toISOString()}
                $isCurrentMonth={day.isCurrentMonth}
                today={isToday(day.date)}
                $isClicked={isClicked}
                $last={data.length === index + 1 && isToday(yesterday)}
                todayCurrent={isToday(day.date) || isToday(toMorrow)}
                onClick={() => {
                  if (isClicked) {
                    setData(day.date.toISOString());
                    setTalhao(item.talhaoId!);
                    setTalhaoCode(item.talhao!);
                    setIsOpen(true);
                  }
              }}
              >
                {
                  isClicked && (
                    <ContainerStatus>
                      {item.totalAreaAtrasada > 0 && statusAtrasado && <Status color="#f44336">{item.totalAreaAtrasada} ha</Status>}
                      {item.totalAreaExecutada > 0 && statusExecutado && <Status color="#4caf50">{item.totalAreaExecutada} ha</Status>}
                      {item.totalAreaExecutadaParcial > 0 && statusExecutadoParcialmente && <Status color="#ff9800">{item.totalAreaExecutadaParcial} ha</Status>}
                      {item.totalAreaPlanejada > 0 && statusPlanejado && <Status color="#2196f3">{item.totalAreaPlanejada} ha</Status>}
                    </ContainerStatus>
                  )
                }
              </DayCell>
          );
          })}
        </DaysGrid>
        ))}
      </WrapperDaysGrid>
      }
    </Container>
  );
};

export default Calendar;
